import { Button, Grid, MenuItem, Select, Switch, Tooltip, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import moment from 'moment';
import React from 'react';

import { IStreamAdFilledInVideoUnitData } from '~/src/dash/api/live_logs/models';
import { GetInVideoUnitLiveLogsData } from '~/src/dash/api/live_logs/stream';
import { EBool } from '~/src/dash/api/types/bool';
import InputGridRow from '~/src/dash/components/input-grid-row';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { ModelPrimaryContainerV2 } from '~/src/dash/components/model-grid-container-v2';
import { VastEditor } from '~/src/dash/components/vast-editor';
import { IBaseState, IWithStylesProps } from '~/src/dash/types/props';
import { formatTime } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { smallRadius } from '~/src/dash/utils/material-dashboard-react';
import { highlight, languages } from '~/src/prismjs';

// COMPONENT DESCRIPTION: TrackerEvents is used to generate tracker event boxes for pixel tracker
// events which are currently in BrandSafetyEvent's and Network's. ValidatePixelURL is used optionally
// if the pixel's macros need to be valid publica macros

export interface ITrackerEvent {
  Active: number;
  ID: number;
  EventType: number;
  PixelURL: string;
  Name: string;
  FireOncePerPod: number;
}

export interface ITrackerEventType {
  Name: string;
  Value: number;
}

interface IProps extends IWithStylesProps {
  title: string;
  // Tracker events attached to brand safety tracker or network tracker
  trackerEvents: ITrackerEvent[];
  // Options available for tracker event's type
  trackerEventTypes: ITrackerEventType[];

  // Handlers
  handleTrackerEventTypeChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => void;
  handleTrackerEventPixelURLChange: (text: string, index: number) => void;
  handleTrackerFireOncePerPodChange: (index: number) => void;
  handleDeleteModelWithPromise: (index: number) => void;
  handleDeleteSuccess: (index: number) => void;
  handleDeleteError: (index: number) => void;
  updateTrackerURLsWithErrors: (action: string, urlID: number) => void;

  // Macros;
  displaySupportedWikiLink: boolean;

  // Needed for in video unit live logs
  enableInVideoUnit?: boolean;
  username?: string;
  publisherID?: string;
  publisherTimezone?: string;
}

interface IState extends IBaseState {
  // delete confirmation popup
  deletePopupActive: boolean;
  deletePopupEventIndex: number;
  deletePopupEventID: number;
  deletePopupEventName: string;
  // for in video units
  inVideoLiveLogs: IStreamAdFilledInVideoUnitData[][];
  refreshingLiveLogs: boolean;
  refreshingLiveLogsTimestamp: string;
}

export const TrackerEvents = decorate(
  {
    styles: () => ({
      fullWidth: { width: '100%' },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      // delete confirmation popup
      deletePopupActive: false,
      deletePopupEventIndex: 0,
      deletePopupEventID: 0,
      deletePopupEventName: '',
      // for in video units
      inVideoLiveLogs: [] as IStreamAdFilledInVideoUnitData[][],
      refreshingLiveLogs: false,
      refreshingLiveLogsTimestamp: '',
    };

    componentDidMount() {
      this.getLiveLogs();
    }

    getLiveLogs(): void {
      if (!this.props.enableInVideoUnit) {
        return;
      }
      this.setState(
        {
          refreshingLiveLogs: true,
        },
        () => {
          if (this.props.username && this.props.publisherID) {
            const inVideoLiveLogs = [] as IStreamAdFilledInVideoUnitData[][];
            const promises = [];
            for (const inVideoUnit of this.props.trackerEvents) {
              promises.push(
                GetInVideoUnitLiveLogsData(
                  inVideoUnit.ID,
                  this.props.publisherID,
                  this.props.username,
                ),
              );
            }
            Promise.all(promises).then((resp) => {
              for (let i = 0; i < resp.length; i++) {
                inVideoLiveLogs[i] = resp[i].inVideoUnit;
              }
              this.setState({
                inVideoLiveLogs,
                refreshingLiveLogs: false,
                refreshingLiveLogsTimestamp: formatTime(
                  moment(),
                  this.props.publisherTimezone || 'UTC',
                ),
              });
            });
          }
        },
      );
    }

    // executed on close tracker, resets model info and calls handler
    onCloseModel = (type: string): void => {
      switch (type) {
        case 'delete':
          this.props.handleDeleteSuccess(this.state.deletePopupEventIndex);
          break;
        case 'error':
          this.props.handleDeleteError(this.state.deletePopupEventIndex);
          break;
        default:
          break;
      }
      this.setState({
        deletePopupActive: false,
        deletePopupEventIndex: 0,
        deletePopupEventID: 0,
        deletePopupEventName: '',
      });
    };

    // opens the model
    openModelDialog = (index: number): void => {
      const deletePopupEvent = this.props.trackerEvents[index];
      if (deletePopupEvent) {
        this.setState({
          deletePopupActive: true,
          deletePopupEventIndex: index,
          deletePopupEventID: deletePopupEvent.ID,
          deletePopupEventName: deletePopupEvent.Name,
        });
      }
    };

    render() {
      const { deletePopupActive, deletePopupEventName, deletePopupEventID } = this.state;
      const {
        trackerEvents,
        trackerEventTypes,
        title,
        handleTrackerEventTypeChange,
        handleTrackerEventPixelURLChange,
        handleTrackerFireOncePerPodChange,
        handleDeleteModelWithPromise,
      } = this.props;
      return (
        <Grid item>
          <Typography style={{ marginBottom: 10, fontWeight: 500, fontSize: 18 }}>
            {title}
          </Typography>

          <Grid
            container
            direction='column'
            justify='flex-start'
            alignItems='flex-start'
            spacing={0}
            className={this.props.classes.fullWidth}
          >
            {trackerEvents.map((trackerEvent: ITrackerEvent, index: number) => {
              return (
                <Grid
                  item
                  style={{
                    width: '100%',
                    marginTop: index === 0 ? '0px' : '1rem',
                  }}
                >
                  <ModelPrimaryContainerV2>
                    <Grid container direction='row'>
                      <Grid item className={this.props.classes.fullWidth}>
                        {/* EVENT SELECT */}
                        <InputGridRow
                          text={
                            trackerEvent.ID === 0
                              ? 'New Event'
                              : 'Event [' + trackerEvent.ID.toString() + ']'
                          }
                        >
                          <Grid container>
                            <Grid item xs={10}>
                              <Select
                                style={{
                                  width: '100%',
                                }}
                                value={trackerEvent.EventType}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                                ) => handleTrackerEventTypeChange(e, index)}
                              >
                                {trackerEventTypes.map((event) => {
                                  return <MenuItem value={event.Value}>{event.Name}</MenuItem>;
                                })}
                              </Select>
                            </Grid>
                            <Grid item xs={1} />
                            <Grid item xs={1}>
                              <Button onClick={() => this.openModelDialog(index)}>
                                <DeleteIcon style={{ color: '#707070' }} />
                              </Button>
                            </Grid>
                          </Grid>
                        </InputGridRow>
                        <VastEditor
                          url={trackerEvent.PixelURL}
                          urlID={trackerEvent.ID}
                          highlight={(code) => highlight(code, languages.elm, 'elm')}
                          padding={5}
                          maxLength={2000}
                          onURLChange={(text: string) => {
                            handleTrackerEventPixelURLChange(text, index);
                          }}
                          textStyle={{
                            background: 'aliceblue',
                            borderRadius: smallRadius,
                            opacity: '1',
                            marginBottom: '5px',
                            padding: '5px 10px',
                          }}
                          wikiMsgHidden={false}
                          disabled={false}
                          brandSafety={true}
                          sendErrorsToParent={this.props.updateTrackerURLsWithErrors}
                        />
                        {handleTrackerFireOncePerPodChange && (
                          <Grid item>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                marginBottom: '10px',
                                width: '400px',
                              }}
                            >
                              <Switch
                                checked={EBool.toBool(trackerEvent.FireOncePerPod)}
                                disabled={false}
                                onChange={() => handleTrackerFireOncePerPodChange(index)}
                                color='secondary'
                                size='small'
                              />
                              <Typography variant='subtitle2' style={{ paddingLeft: '8px' }}>
                                Fire Once Per Pod
                              </Typography>
                              <Tooltip title='This tracker will only fire on the first ad slot of each pod.'>
                                <InfoIcon
                                  style={{
                                    verticalAlign: 'bottom',
                                    width: '1rem',
                                    color: '#929599',
                                    marginLeft: '5px',
                                  }}
                                />
                              </Tooltip>
                            </div>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </ModelPrimaryContainerV2>
                </Grid>
              );
            })}
          </Grid>

          {/* DELETE CONFIRMATION POPUP*/}
          <ModelDialogDeleteView
            active={deletePopupActive}
            model='Event'
            ID={deletePopupEventID}
            Name={deletePopupEventName}
            deleteModel={handleDeleteModelWithPromise}
            onDelete={() => this.onCloseModel('delete')}
            onError={() => this.onCloseModel('error')}
            onClose={() => this.onCloseModel('close')}
          />
        </Grid>
      );
    }
  },
);
